import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Loader2, Trash2, ImageUp } from "lucide-react";
import { api, getToken } from "../../../software/api";

const B = process.env.REACT_APP_BACKEND_URL;
const send = async (method, path, fd) => {
  const r = await fetch(`${B}/api${path}`, { method, headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Couldn't save the brand");
  return d;
};

function BrandRow({ b, pads, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const replace = async (f) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await send("PUT", `/test-strips/brands/${b.id}`, fd); toast.success(`${b.name} chart updated`); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove the ${b.name} test strip brand?`)) return;
    try { await api("DELETE", `/test-strips/brands/${b.id}`); onChanged(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100" data-testid={`strip-brand-row-${b.id}`}>
      <a href={`${B}${b.chart_url}`} target="_blank" rel="noreferrer"><img src={`${B}${b.chart_url}`} alt={b.name} className="h-14 w-20 object-cover rounded-sm border border-slate-300 bg-white" /></a>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-black text-[#123a63]">{b.name}</div>
        <div className="text-[11px] text-slate-500 truncate">{b.pads.map((p) => pads[p.key]?.label || p.key).join(" · ")}</div>
      </div>
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) replace(f); }} data-testid={`strip-brand-replace-input-${b.id}`} />
      <button onClick={() => ref.current.click()} disabled={busy} title="Replace chart image" data-testid={`strip-brand-replace-${b.id}`} className="p-1.5 rounded-sm border border-[#7ba7d9] text-[#123a63] hover:bg-[#dbe9f7]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />}</button>
      <button onClick={del} title="Remove brand" data-testid={`strip-brand-delete-${b.id}`} className="p-1.5 rounded-sm border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function StripBrands({ brands, pads, onChanged, onClose }) {
  const [name, setName] = useState(""), [file, setFile] = useState(null), [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim() || !file) { toast.error("Enter the brand name and pick the colour chart image"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("name", name.trim()); fd.append("file", file);
      const b = await send("POST", "/test-strips/brands", fd);
      toast.success(`${b.name} saved — ${b.pads.length} pads found on the chart`); setName(""); setFile(null); onChanged(b);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-3" data-testid="strip-brands-modal">
      <div className="w-full max-w-lg bg-white border border-[#7ba7d9] rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-4 py-2.5 border-b border-[#7ba7d9] bg-[#dbe9f7] rounded-t-xl">
          <span className="font-black text-[#123a63]">Test Strip Brands</span>
          <button onClick={onClose} data-testid="strip-brands-close" className="rounded-full p-1 hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[12px] text-slate-600">Save a clear, straight-on image of each brand's colour chart. Strip photos are then compared against it — no bottle needed in the photo.</p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Brand name e.g. Aussie Gold 6-in-1" data-testid="strip-brand-name"
              className="border border-[#7ba7d9] rounded-sm px-2 py-1.5 text-[13px] outline-none focus:border-[#123a63]" />
            <label className="px-3 py-1.5 rounded-sm border border-[#7ba7d9] text-[12px] font-bold text-[#123a63] cursor-pointer hover:bg-[#dbe9f7] max-w-[9rem] truncate">
              {file ? file.name : "Chart image…"}
              <input type="file" accept="image/*" hidden onChange={(e) => setFile(e.target.files[0] || null)} data-testid="strip-brand-file" />
            </label>
            <button onClick={add} disabled={busy} data-testid="strip-brand-add" className="px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-black flex items-center gap-1 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}{busy ? "Reading chart…" : "Add"}
            </button>
          </div>
          <div data-testid="strip-brand-list">
            {brands.length ? brands.map((b) => <BrandRow key={b.id} b={b} pads={pads} onChanged={() => onChanged()} />)
              : <p className="text-[12px] text-slate-400 py-3 text-center">No brands saved yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
